/**
 * Agent action-receipt WIRE contract (v1).
 *
 * The browser-safe, versioned shape an agent emits over the chat SSE stream to
 * declare what a tool committed, so the frontend can render an origin-aware
 * visual effect (create / update / move / delete highlight). It is a transport
 * contract — distinct from, and mappable from, the persistence-side
 * `ActionReceiptV1` (ADR-040) which `@xavior/persistence` owns. This package is
 * a pure leaf (no `@xavior/*` deps); `apps/web` may import it, and MUST NOT
 * import `@xavior/persistence`, so the wire type lives here.
 *
 * `parseAgentActionReceipt` is the runtime decoder used at the SSE boundary:
 * the live chat stream is consumed as `any`, so a structural validator is
 * required before an untrusted payload is allowed to drive UI state. It is
 * defensive by construction — it rebuilds a normalized object from known fields
 * only (dropping anything unexpected), caps array/string sizes, and returns
 * `null` for anything that is not a well-formed v1 receipt.
 */
/** Hard caps — this validator is a security boundary against untrusted SSE payloads. */
export const MAX_RECEIPT_EFFECTS = 200;
export const MAX_LABEL_LEN = 200;
export const MAX_CHANGED_FIELDS = 64;
export const MAX_CHANGED_FIELD_LEN = 64;
const EFFECT_KINDS = new Set([
    'created', 'updated', 'deleted', 'restored', 'submitted', 'read',
]);
const STATUSES = new Set([
    'succeeded', 'failed', 'indeterminate',
]);
const ORIGINS = new Set(['chat', 'run']);
const LIFECYCLES = new Set([
    'active', 'archived', 'deleted', 'accepted_by_provider',
]);
const MOVE_DIMENSIONS = new Set([
    'calendar-time', 'board-column', 'order', 'folder', 'other',
]);
function isRecord(v) {
    return typeof v === 'object' && v !== null && !Array.isArray(v);
}
function nonEmptyString(v) {
    return typeof v === 'string' && v.length > 0;
}
function finiteNumber(v) {
    return typeof v === 'number' && Number.isFinite(v);
}
function capString(v, max) {
    return v.length > max ? v.slice(0, max) : v;
}
function parseAnchor(input) {
    if (!isRecord(input))
        return undefined;
    const out = {};
    if (nonEmptyString(input.containerId))
        out.containerId = capString(input.containerId, MAX_LABEL_LEN);
    if (finiteNumber(input.order))
        out.order = input.order;
    if (nonEmptyString(input.startAt))
        out.startAt = capString(input.startAt, MAX_LABEL_LEN);
    if (nonEmptyString(input.endAt))
        out.endAt = capString(input.endAt, MAX_LABEL_LEN);
    return Object.keys(out).length > 0 ? out : undefined;
}
function parseMovement(input) {
    if (!isRecord(input))
        return undefined;
    if (!MOVE_DIMENSIONS.has(input.dimension))
        return undefined;
    const out = { dimension: input.dimension };
    const from = parseAnchor(input.from);
    const to = parseAnchor(input.to);
    if (from)
        out.from = from;
    if (to)
        out.to = to;
    return out;
}
function parsePresentation(input) {
    if (!isRecord(input))
        return undefined;
    const out = {};
    if (nonEmptyString(input.safeLabel))
        out.safeLabel = capString(input.safeLabel, MAX_LABEL_LEN);
    if (Array.isArray(input.changedFields)) {
        const fields = input.changedFields
            .filter(nonEmptyString)
            .slice(0, MAX_CHANGED_FIELDS)
            .map((f) => capString(f, MAX_CHANGED_FIELD_LEN));
        if (fields.length > 0)
            out.changedFields = fields;
    }
    const movement = parseMovement(input.movement);
    if (movement)
        out.movement = movement;
    return Object.keys(out).length > 0 ? out : undefined;
}
function parseEffect(input) {
    if (!isRecord(input))
        return null;
    if (!nonEmptyString(input.effectId))
        return null;
    if (!finiteNumber(input.seq))
        return null;
    if (!EFFECT_KINDS.has(input.kind))
        return null;
    if (!isRecord(input.resource))
        return null;
    if (!nonEmptyString(input.resource.type) || !nonEmptyString(input.resource.id))
        return null;
    const effect = {
        effectId: input.effectId,
        seq: input.seq,
        kind: input.kind,
        resource: { type: input.resource.type, id: input.resource.id },
    };
    if (nonEmptyString(input.resource.instanceId)) {
        effect.resource.instanceId = capString(input.resource.instanceId, MAX_LABEL_LEN);
    }
    if (finiteNumber(input.versionBefore))
        effect.versionBefore = input.versionBefore;
    if (finiteNumber(input.versionAfter))
        effect.versionAfter = input.versionAfter;
    if (LIFECYCLES.has(input.lifecycleAfter)) {
        effect.lifecycleAfter = input.lifecycleAfter;
    }
    const presentation = parsePresentation(input.presentation);
    if (presentation)
        effect.presentation = presentation;
    return effect;
}
/**
 * Validate + normalize an untrusted value into an `AgentActionReceiptWireV1`,
 * or return `null`. Pure, dependency-free, defensive: rebuilds from known fields
 * only and enforces the size caps above.
 */
export function parseAgentActionReceipt(input) {
    if (!isRecord(input))
        return null;
    if (input.schemaVersion !== 1)
        return null;
    if (!nonEmptyString(input.receiptId))
        return null;
    if (!nonEmptyString(input.executionId))
        return null;
    if (!nonEmptyString(input.workspaceId))
        return null;
    if (!nonEmptyString(input.completedAt))
        return null;
    if (!ORIGINS.has(input.origin))
        return null;
    if (!STATUSES.has(input.status))
        return null;
    if (!isRecord(input.tool) || !nonEmptyString(input.tool.name) || !nonEmptyString(input.tool.callId)) {
        return null;
    }
    if (!Array.isArray(input.effects))
        return null;
    if (input.effects.length > MAX_RECEIPT_EFFECTS)
        return null;
    const effects = [];
    for (const raw of input.effects) {
        const parsed = parseEffect(raw);
        if (!parsed)
            return null;
        effects.push(parsed);
    }
    // A succeeded mutating action must declare ≥1 effect; failed/indeterminate may be empty.
    if (input.status === 'succeeded' && effects.length === 0)
        return null;
    const receipt = {
        schemaVersion: 1,
        receiptId: input.receiptId,
        executionId: input.executionId,
        workspaceId: input.workspaceId,
        origin: input.origin,
        completedAt: input.completedAt,
        tool: { name: input.tool.name, callId: input.tool.callId },
        status: input.status,
        effects,
    };
    if (nonEmptyString(input.conversationId))
        receipt.conversationId = input.conversationId;
    if (isRecord(input.sequence) &&
        finiteNumber(input.sequence.turn) &&
        finiteNumber(input.sequence.action)) {
        receipt.sequence = { turn: input.sequence.turn, action: input.sequence.action };
    }
    if (isRecord(input.summary) && finiteNumber(input.summary.count)) {
        receipt.summary = { count: input.summary.count };
        if (nonEmptyString(input.summary.safeText)) {
            receipt.summary.safeText = capString(input.summary.safeText, MAX_LABEL_LEN);
        }
    }
    if (isRecord(input.error) && nonEmptyString(input.error.code)) {
        receipt.error = { code: input.error.code };
        if (nonEmptyString(input.error.message)) {
            receipt.error.message = capString(input.error.message, MAX_LABEL_LEN);
        }
    }
    return receipt;
}
//# sourceMappingURL=agent-action-receipt.js.map